import i18n from "@plugins/i18n";
import DynamicLinkUtils from "@plugins/dynamic-link/dynamic.link.util";
import RichTextUtils from "@plugins/richtext/utils/richtext.utils";
import { nextTick } from "vue";
import { SELECTOR } from "@plugins/dynamic-link/dynamic.link.constant";
import DOMUtils from "@utils/dom.utils";
import NotebookDynamicInternalLinkModal from "@plugins/dynamic-link/components/NotebookDynamicInternalLinkModal.vue";

export default function (editor) {
  const menuButtonName = "notebook_dynamic_link";
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;

  let editModalComponent;
  const linkComponents = [];
  let modalId = null;

  // init
  (function init() {
    // register the plugin.
    richTextEditor.plugins["notebook_dynamic_link"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      destroy,
    };

    // create a hidden edit modal
    nextTick(() => {
      if (richTextEditor.$el?.parentNode) {
        editModalComponent = DynamicLinkUtils.createEditModal(
          richTextEditor.$el.parentNode,
          richTextEditor,
        );
      }
    });
  })();

  function addExternalLinkClickListener(aTags = []) {
    aTags?.forEach((aTag) => {
      aTag?.addEventListener("click", ($event) => {
        $event.stopPropagation();
        editModalComponent.setModalContent(aTag.href);
        editModalComponent.setEventListener("delete", () => {
          const content = aTag.textContent;
          const el = DynamicLinkUtils.createTextNode(content);
          aTag.replaceWith(el);
          editModalComponent.$el.blur();
          editorSave(0);
        });
        editModalComponent.setEventListener("edit", () => {
          handleClickExternalLink();
        });
        editModalComponent.showAt(aTag);
      });
    });
  }

  function createLinkComponentApp(wrapper, linkObject, introductionTitle) {
    const linkApp = DynamicLinkUtils.createDynamicInternalLinkVueApp(
      {
        isEditMode: true, // always in editor component edit mode
        anchor: linkObject.anchor,
        customText: linkObject.customText,
        dataLinkType: linkObject.dataLinkType,
        introductionTitle,
      },
      {
        onClick: ({ $event, displayLabel, clickLinkObject, rootElement, handleEdit }) => {
          // always in editor component edit mode
          $event?.stopPropagation();
          editModalComponent.setModalContent(displayLabel);
          editModalComponent.setEventListener("delete", () => {
            const el = DynamicLinkUtils.createTextNode(displayLabel);
            wrapper.replaceWith(el);
            editModalComponent.$el.blur();
            editorSave(0);
          });
          editModalComponent.setEventListener("edit", () => {
            openModal(clickLinkObject).then(({ anchor, customText, dataLinkType }) => {
              handleEdit({ anchor, customText, dataLinkType });
              editorSave(100);
            });
          });
          editModalComponent.showAt(rootElement.value);
        },
      },
      richTextEditor,
    );
    return linkApp;
  }

  function renderInternalLink() {
    const linkTags = DynamicLinkUtils.getLinkTagsFromContainer(editor.getBody());
    const introductionTitle = i18n.t(
      "dynamic_internal_link_section_activity_dropdown_introduction",
    );
    linkTags.forEach((element) => {
      // create dynamic internal link component
      const linkObject = DynamicLinkUtils.retrieveDataFromLinkTag(element);

      // wrapper
      const wrapper = DynamicLinkUtils.createWrapper(false);
      // wrap link tag with wrapper
      element.parentNode.insertBefore(wrapper, element);
      wrapper.appendChild(element);
      // mount app to link tag
      const linkApp = createLinkComponentApp(wrapper, linkObject, introductionTitle);
      const linkComponent = linkApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(linkComponent);
      // add space and new line for user to edit in same or next line
      nextTick(() => {
        RichTextUtils.addNonBreakingSpaceIfIsLastElement(wrapper);
      });
      RichTextUtils.addNewLineIfIsLastElement(wrapper);
      linkComponents.push(linkComponent);
    });
  }

  // rendering hook
  function renderInEditor() {
    renderInternalLink();
  }

  // converting dynamic link component to save format
  function cleanUp(content) {
    const container = document.createElement("div");
    container.innerHTML = content;
    const clone = container.cloneNode(true);
    DynamicLinkUtils.getComponentsFromContainer(clone).forEach((componentElement) => {
      const linkObject = DynamicLinkUtils.retrieveDataFromComponent(componentElement);
      const tag = DynamicLinkUtils.createDynamicLinkTagFrom(linkObject);
      const wrapperElement = DynamicLinkUtils.findWrapper(componentElement);
      if (wrapperElement) {
        wrapperElement.replaceWith(tag);
      } else {
        componentElement.replaceWith(tag);
      }
    });

    return clone.innerHTML;
  }

  function handlePluginRerender(target) {
    const internalLinkElement = target?.closest(SELECTOR.COMPONENT);
    if (internalLinkElement && !internalLinkElement.__vue__) {
      const [component] = handleInternalLinkComponentsRerender([internalLinkElement]);
      return component.$el;
    }
  }

  function handleInternalLinkComponentsRerender(componentElements) {
    const introductionTitle = i18n.t(
      "dynamic_internal_link_section_activity_dropdown_introduction",
    );
    const components = [];
    componentElements.forEach((element) => {
      const linkObject = DynamicLinkUtils.retrieveDataFromComponent(element);
      const wrapperElement = DynamicLinkUtils.findWrapper(element);
      const app = createLinkComponentApp(wrapperElement, linkObject, introductionTitle);
      const linkComponent = app.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(linkComponent);
      components.push(linkComponent);
    });
    return components;
  }

  function destroy() {
    // AppInstanceUtils.unmountFromComponent(editModalComponent);
    // linkComponents.forEach(link => {
    //   AppInstanceUtils.unmountFromComponent(link);
    // });
  }
  function containPluginElements(container) {
    return DynamicLinkUtils.getAllDynamicLinksFromContainer(container)?.length > 0;
  }

  /**
   * Add interceptors before and after external link mceLink Execution
   * Reference: https://www.tiny.cloud/docs/tinymce/6/dialog-configuration/
   * @param customizerFunc return a new open function
   */
  function mceLinkCommandCustomizer(customizerFunc) {
    const originalMceLinkFunc = editor.windowManager.open;
    if (customizerFunc) {
      editor.windowManager.open = customizerFunc(originalMceLinkFunc);
    }
    return originalMceLinkFunc;
  }

  // for external link
  function handleClickExternalLink() {
    const title = i18n.t("dynamic_external_link_add_edit_title");
    const customizerFunc = (originalMceLinkFunc) => {
      return (dialogOption) => {
        // execute before original call
        // update Dialog Title
        dialogOption.title = title;
        // add post hook to onSubmit to add click listener to newly created external link
        const submitF = dialogOption.onSubmit;
        dialogOption.onSubmit = function (api) {
          submitF.apply(this, [api]);
          const href = api.getData()?.url?.value ?? "";
          nextTick(() => {
            const aTags = DynamicLinkUtils.findExternalLinkATags(editor.getBody(), href);
            addExternalLinkClickListener(aTags);
          });
        }.bind(dialogOption);
        // call the original Function to execute mceLink
        originalMceLinkFunc.apply(this, [dialogOption]);
        nextTick(() => {
          editor.windowManager.open = originalMceLinkFunc;
        });
      };
    };

    // update this keyword in function
    customizerFunc.bind(editor.windowManager);
    mceLinkCommandCustomizer(customizerFunc);
    editor.execCommand("mceLink");
  }

  function handleClickNotebookLink() {
    const selection = editor.selection;
    let customText = "";
    if (!selection.isCollapsed()) {
      // has texts highlighted by cursor
      customText = selection.getContent({ format: "text" });
    }

    openModal({ anchor: null, customText }).then((newLinkObject) => {
      handleSave(newLinkObject, selection);
    });
  }

  function openModal(linkObject) {
    return new Promise((resolve) => {
      const notebookUuid = context.notebookUuid;
      modalId = richTextEditor.$modal.show(
        NotebookDynamicInternalLinkModal,
        {
          linkObject,
          notebookUuid,
        },
        {
          save: (newLinkObject) => {
            resolve(newLinkObject);
          },
        },
        {},
        true,
      );
    });
  }
  function handleSave(newLinkObject, selection) {
    richTextEditor.$modal.remove(modalId);
    if (selection) {
      const tag = DynamicLinkUtils.createDynamicLinkTagFrom(newLinkObject);
      if (!selection.isCollapsed()) {
        // has texts highlighted by cursor
        // the selected content is removed from its node(s) and becomes dynamic link
        // use native Range Api https://developer.mozilla.org/en-US/docs/Web/API/Range
        const range = selection.getRng();
        range.deleteContents();
        range.insertNode(tag);
      } else {
        editor.insertContent(tag.outerHTML);
      }
      renderInEditor();
      editorSave(100);
    }
  }

  // defer editor save after DynamicInternalLink has dynamic component fully loaded
  function editorSave(delayMs = 100) {
    setTimeout(() => {
      editor.save();
    }, delayMs);
  }
  // add UI button
  editor.ui.registry.addMenuButton(menuButtonName, {
    tooltip: i18n.t("tinymce_plugin_dynamic_link_icon_button_tooltip"),
    icon: "link",
    fetch: (callback) => {
      const items = [
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_dynamic_link_external_link_button_tooltip"),
          onAction: handleClickExternalLink,
        },
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_dynamic_link_from_notebook_button_tooltip"),
          onAction: handleClickNotebookLink,
        },
      ];
      callback(items);
    },
  });

  // editor click event
  editor.on("click", (event) => {
    event.stopPropagation();
    const rerenderedElement = handlePluginRerender(event.target);
    if (rerenderedElement) {
      nextTick(() => {
        //continue click action after rendering the target if exists
        rerenderedElement.click();
      });
    }
  });
}
